<template>
    <div class="position-relative" ref="container">
        <div class="d-flex">
            <!-- Searchbar -->
            <div class="flex-grow-1">
                <div class="my-0">
                    <span class="icon icon-glyphicons-basic-search search-icon align-middle"></span>
                    <input
                        class="form-control rounded-lg search-input"
                        :class="{
                            'search-input-constrained': constrainWidth,
                            'w-100': !constrainWidth,
                        }"
                        type="text"
                        :placeholder="placeholder"
                        aria-label="Search library"
                        v-model="searchQuery"
                        @input="search"
                        @focus="setFocusState(true)"
                        ref="searchInput"
                    />
                </div>
            </div>
            <!-- Loading Spinner -->
            <div class="flex-grow-0">
                <div
                    v-show="showLoadingSpinner"
                    class="spinner-container justify-content-center align-items-center"
                    :class="{ 'd-flex': showLoadingSpinner }"
                >
                    <span class="mx-1">
                        <span
                            class="spinner-border spinner-border-sm text-primary"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        <span class="sr-only">Loading...</span>
                    </span>
                </div>
            </div>
        </div>
        <!-- Results Dropdown -->
        <div
            class="position-absolute shadow-sm flex-column results-container"
            v-show="showResultsDropdown"
            :class="{
                'd-flex': showResultsDropdown,
                'results-container-constrained': constrainWidth,
                'w-100': !constrainWidth,
            }"
        >
            <!-- Empty Display -->
            <div
                v-show="noResults"
                :class="{ 'd-flex': noResults }"
                class="flex-grow-1 justify-content-center align-items-center results-empty"
            >
                <div class="font-weight-500 text-secondary">No results matching your query</div>
            </div>
            <!-- Results List -->
            <div v-show="!noResults">
                <div
                    class="d-flex align-items-center results-row"
                    role="button"
                    v-for="item in results"
                    :key="item.id"
                    @click="selectItem(item)"
                >
                    <!-- Item Icon -->
                    <div class="flex-grow-0 item-icon">
                        <library-case-card
                            v-if="['owned_case', 'community_reference'].includes(item.item_type)"
                            :case-obj="item.case"
                            size="small"
                        ></library-case-card>
                        <div v-else class="d-flex justify-content-center align-items-center folder-container">
                            <span class="icon icon-glyphicons-basic-folder folder-icon"></span>
                        </div>
                    </div>
                    <!-- Item Info -->
                    <div class="flex-grow-1 d-flex align-items-center pr-2">
                        <div>
                            <div class="small max-one-line font-weight-500 search-text">{{ item.title }}</div>
                            <div class="tiny text-secondary max-one-line search-text">{{ item.description }}</div>
                            <div class="d-flex align-items-center">
                                <div>
                                    <span class="badge badge-light case-badge align-middle"> Library </span>
                                </div>
                                <div v-for="(folderName, index) in item.parent_folder_name_list" :key="index">
                                    <span class="icon icon-xs icon-glyphicons-basic-chevron-right chevron mx-1"></span>
                                    <span class="badge badge-light case-badge align-middle">
                                        {{ trimString(folderName, 20) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import debounce from "lodash.debounce"
import Utilities from "@/assets/js/utilities.js"
import LibraryCaseCard from "@/components/LibraryCaseCard"

export default {
    name: "LibraryItemSearch",
    components: {
        LibraryCaseCard,
    },
    props: {
        // When true, the input is be cleared after an item is selected.
        clearAfterSelection: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Constrains the width of the input - could be replaced in the future by passing in
        // an exact width in pixels.
        constrainWidth: {
            type: Boolean,
            required: false,
            default: false,
        },
        placeholder: {
            type: String,
            required: false,
            default: "Search folders and scenarios..."
        },
        itemTypes: {
            type: Array,
            required: false,
        },
    },
    data() {
        return {
            isLoading: false,
            isFocussed: false,
            isExpanding: false,
            searchQuery: "",
            searchId: null,
            results: [],
            resultsLoaded: false,
        }
    },
    computed: {
        organizationId() {
            return this.$store.getters.currentOrganizationId
        },
        showResultsDropdown() {
            return this.isFocussed && this.resultsLoaded
        },
        showLoadingSpinner() {
            // Don't show the loading spinner while the searchbar is expanding (looks too chaotic).
            return this.isLoading && !this.isExpanding
        },
        noResults() {
            return this.results.length === 0 && this.resultsLoaded
        },
    },
    watch: {
        isFocussed: function(newValue, oldValue) {
            if (newValue === true && oldValue === false) {
                this.isExpanding = true
                let vm = this
                window.setTimeout(() => {
                    vm.isExpanding = false
                }, 200)
                // Trigger a search to fetch an initial set of results.
                this.search()
            }
        }
    },
    methods: {
        // Trigger a debounced search of library items.
        search() {
            this.isLoading = true
            this.searchDebounced()
        },
        // Debounced fetch of library items.
        searchDebounced: debounce(async function () {
            // Store a random search to make sure that an older search query doesn't
            // overwrite new search results.
            let searchId = Utilities.getRandomIdStr(10)
            this.searchId = searchId
            let vm = this
            // Construct query parameters.
            let queryParams = {
                organizationId: vm.organizationId,
                query: vm.searchQuery,
            }
            if (this.itemTypes) {
                queryParams.itemTypes = this.itemTypes.join(",")
            }
            http.get("/api/library-items/search/", {
                params: queryParams
            })
                .then(function (response) {
                    if (searchId === vm.searchId) {
                        vm.results = response.data
                        vm.resultsLoaded = true
                    }
                })
                .finally(() => {
                    if (searchId === vm.searchId) {
                        vm.isLoading = false
                    }
                })
        }, 150),
        // Bring the searchbar into focus.
        focus() {
            this.$refs.searchInput.focus()
        },
        // Set the focus state of the searchbar.
        setFocusState(state) {
            this.isFocussed = state
            // Clear results when the searchbar is closed.
            if (state === false) {
                this.results = []
            }
        },
        // Callback used to determine if a click originated outside the searchbar or results.
        handleClickOutside(event) {
            if (!this.$refs.container.contains(event.target)) {
                // Click was outside the component, so remove focus on the search input.
                this.isFocussed = false
            }
        },
        // Get a display name for an item.
        getItemTypeName(item) {
            return itemNames[item.item_type]
        },
        // Handle selection of an item.
        selectItem(item) {
            this.$emit("selected", item)
            if (this.clearAfterSelection) {
                // Remove focus and clear the search input.
                this.isFocussed = false
                this.searchQuery = ""
            }
        },
        // Trim a string to the desired length, for display.
        trimString(str, length) {
            return Utilities.trimString(str, length)
        },
    },
    mounted() {
        window.addEventListener("click", this.handleClickOutside)
    },
    beforeDestroy() {
        window.removeEventListener("click", this.handleClickOutside)
    },
}

// Display names for each item type.
const itemNames = {
    owned_case: "Scenario",
    community_reference: "Community Scenario",
    folder: "Folder",
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

$inactive-width: 300px;
$active-width: 320px;

.search-input {
    padding-left: 30px;
    margin-top: -5px;
    margin-bottom: -5px;
    height: 36px;
    font-size: 15px;
    background-color: $search-bg;
    transition: all 0.2s ease-in-out;
    &:not(:focus) {
        border-color: transparent;
    }
}

.search-input-constrained {
    width: $inactive-width;
    @media (min-width: $breakpoint-md) {
        &:focus {
            width: $active-width;
        }
    }
}

.results-container {
    top: 36px;
    background: #ffffff;
    border-radius: $border-radius-lg;
    border: 1px solid $gray-300;
    overflow: hidden;
    z-index: $zindex-modal;
}

.results-container-constrained {
    width: 500px !important;
}

.results-empty {
    min-height: 100px;
}

.results-row {
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 12px;
    padding-bottom: 12px;
    background-color: #ffffff;
    &:hover {
        background-color: $gray-100;
    }
}

.item-icon {
    padding-right: 18px;
}

.folder-container {
    height: $lib-item-height-sm;
    width: $lib-item-width-sm;
    background-color: $light;
    border-radius: $border-radius-sm;
}

.folder-icon {
    color: $folder-color;
    font-size: 28px;
    left: 10px;
}

.search-icon {
    position: absolute;
    color: $gray-600;
    left: 10px;
    top: 5px;
    font-size: 16px;
}

.chevron {
    color: $gray-550;
}

.spinner-container {
    position: absolute;
    right: 10px;
}

.spinner-border-sm {
    border-width: 0.15em;
}

.search-text {
    margin-bottom: 2px;
}
</style>